import { SearchSelectV2 } from '@app/components/ui/SearchSelectV2';
import { captureError } from '@app/lib/Sentry';
import { InputProps, SelectProps } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Generic types for the API response structure
export interface ApiResponse<T> {
  data: T[];
  total: number;
  offset: number;
}

export interface ApiQueryParams {
  limit: number;
  offset: number;
  query?: string;
}

// Generic API function type
export type ApiFetchFunction<T> = (params: ApiQueryParams) => Promise<{ data: ApiResponse<T> }>;

// Generic item can be any object - we'll extract ID and label via functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SelectableItem = Record<string, any>;

type GenericSearchSelectProps<T extends SelectableItem> = {
  // Required props
  apiFetch: ApiFetchFunction<T>;
  getItemId: (item: T) => number | string;
  getItemLabel: (item: T) => string;
  onSelectionChange?: (selection: T | T[] | undefined) => void;

  // Optional customization
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  searchPlaceholder?: string;
  // If true, skip initial load and fetch on first dropdown open only
  lazyLoadOnOpen?: boolean;
  // Custom option rendering
  renderOption?: (item: T) => React.ReactNode;
  /**
   * Offer only the fetched items this accepts. Absent offers all of them.
   *
   * WHY IT FILTERS THE FETCHED PAGE AND NOT THE COUNTS
   * ─────────────────────────────────────────────────
   * For a narrowing the endpoint has no parameter for — "only simple packages", where
   * `GET /service-packages` has no kind filter — the answer can only be trimmed on arrival. It is
   * applied where the page enters `items` below, while `hasMore`, `totalCount` and the offset stay
   * the SERVER's own figures, deliberately: paging is the server's arithmetic, so a page whose
   * every row this rejects must still lead to the next page rather than ending the scroll. The
   * visible cost is that the footer's "N of M" counts what was fetched, not what is shown.
   *
   * Selected items are NOT filtered — see the options memo. A value a record already holds has to
   * render its own label whatever the current narrowing is, the same reason
   * `selectedItemsProvider` exists.
   *
   * A caller whose predicate is a fresh closure on every render is fine: it is read through a ref,
   * so it never re-triggers a fetch.
   */
  filterItem?: (item: T) => boolean;

  // External selected items provider (avoids syncing via useEffect)
  selectedItemsProvider?: () => T | T[] | undefined;
  // Trigger a reload from parent by changing this value
  reloadSignal?: number | string;

  inputSearchProps?: InputProps;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

export function GenericSearchSelect<T extends SelectableItem>({
  apiFetch,
  getItemId,
  getItemLabel,
  onSelectionChange,
  placeholder = 'Select item',
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  searchPlaceholder,
  reloadSignal,
  inputSearchProps,
  renderOption,
  filterItem,
  selectedItemsProvider,
  lazyLoadOnOpen = false,
  onOpenChange,
  ...selectProps
}: GenericSearchSelectProps<T>) {
  const { t } = useTranslation();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSearch, setCurrentSearch] = useState('');
  const [currentOffset, setCurrentOffset] = useState(0);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const loadingRef = useRef(false);
  const currentSearchRef = useRef('');
  const didMountReloadRef = useRef(false);
  const prevReloadSignalRef = useRef<number | string | undefined>(undefined);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const didLazyLoadRef = useRef(false);

  // In a ref so `loadItems` does not list it as a dependency: a caller passing an inline predicate
  // would otherwise rebuild the loader on every render. See the prop's own note.
  const filterItemRef = useRef(filterItem);
  filterItemRef.current = filterItem;

  // track external controlled value and its previous value
  const controlledValue = selectProps?.value as number | string | (number | string)[] | undefined;
  const prevControlledValueRef = useRef<typeof controlledValue>(controlledValue);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  useEffect(() => {
    currentSearchRef.current = currentSearch;
  }, [currentSearch]);

  const loadItems = useCallback(
    async (search: string = '', offset: number = 0, append: boolean = false) => {
      if (loadingRef.current) return;

      setLoading(true);

      const query: ApiQueryParams = {
        limit: pageSize,
        offset,
      };

      if (search) {
        query.query = search.trim();
      }

      try {
        const response = await apiFetch(query);
        const resp = response?.data ?? { data: [], total: 0, offset: offset };

        const newItems = Array.isArray(resp.data) ? resp.data : [];
        const respOffset = Number.isFinite(resp.offset) ? Number(resp.offset) : offset;
        const respTotal = Number.isFinite(resp.total) ? Number(resp.total) : newItems.length;
        // The narrowing is applied to what is DISPLAYED; every figure below stays the server's, so
        // the paging arithmetic is unaffected by it — see the `filterItem` prop's own note.
        const shown = filterItemRef.current ? newItems.filter(filterItemRef.current) : newItems;
        setItems((prev) => (append ? [...prev, ...shown] : shown));
        setTotalCount(respTotal);
        setHasMore(newItems.length > 0 && respOffset + newItems.length < respTotal);
        setCurrentOffset(respOffset);
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('Failed to fetch items'), {
          query,
          component: 'GenericSearchSelect',
        });
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, apiFetch]
  );

  // Initial load (skip if lazy)
  useEffect(() => {
    if (!lazyLoadOnOpen) {
      loadItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyLoadOnOpen]);

  useEffect(() => {
    if (!didMountReloadRef.current) {
      didMountReloadRef.current = true;
      prevReloadSignalRef.current = reloadSignal;
      return;
    }
    // Only refetch when the reloadSignal value actually changes
    if (reloadSignal !== prevReloadSignalRef.current) {
      prevReloadSignalRef.current = reloadSignal;
      if (reloadSignal !== undefined) {
        loadItems(currentSearchRef.current, 0, false);
      }
    }
  }, [reloadSignal, loadItems]);

  const handleSearch = useCallback(
    (searchTerm: string = '') => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const trimmedSearchTerm = searchTerm.trim();

      // Too short to search: keep currentSearch on the term the shown items were fetched with, or
      // load-more would page them with a query the backend rejects (min 3 chars → 400).
      if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < minSearchLength) {
        return;
      }

      // Set up debounced search
      debounceTimerRef.current = setTimeout(() => {
        setCurrentSearch(trimmedSearchTerm);
        setCurrentOffset(0);
        loadItems(trimmedSearchTerm, 0, false);
      }, searchDebounceMs);
    },
    [loadItems, minSearchLength, searchDebounceMs]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextOffset = currentOffset + pageSize;
      loadItems(currentSearch, nextOffset, true);
    }
  }, [loading, hasMore, currentOffset, pageSize, currentSearch, loadItems]);

  // Trigger first load when dropdown opens (lazy mode only)
  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Forwarded first, and for closing too: a caller that keeps `open` controlled needs every
      // change, not only the one that triggers a lazy load.
      onOpenChange?.(open);
      if (!open) return;
      if (!lazyLoadOnOpen) return;
      if (didLazyLoadRef.current) return;
      didLazyLoadRef.current = true;
      loadItems(currentSearch, 0, false);
    },
    [lazyLoadOnOpen, loadItems, currentSearch, onOpenChange]
  );

  // union map of current page, previously selected, and optional external provider
  const getAllItemsMap = useCallback(() => {
    const map = new Map<number | string, T>();
    items.forEach((it) => map.set(getItemId(it), it));
    selectedItems.forEach((it) => map.set(getItemId(it), it));
    const ext = selectedItemsProvider?.();
    if (ext) {
      (Array.isArray(ext) ? ext : [ext]).forEach((it) => map.set(getItemId(it), it));
    }
    return map;
  }, [items, getItemId, selectedItems, selectedItemsProvider]);

  // resolve selection from union map so it survives searches/pagination
  const handleChange = useCallback(
    (value: (number | string) | (number | string)[] | undefined) => {
      const map = getAllItemsMap();

      if (Array.isArray(value)) {
        const sel = value.map((id) => map.get(id)).filter(Boolean) as T[];
        setSelectedItems(sel);
        onSelectionChange?.(sel);
        return;
      }

      if (value !== undefined) {
        const item = map.get(value);
        if (item) {
          setSelectedItems([item]);
          onSelectionChange?.(item);
        }
        return;
      }

      // cleared
      setSelectedItems([]);
      onSelectionChange?.(undefined);
    },
    [getAllItemsMap, onSelectionChange]
  );

  // only react to external value changes
  useEffect(() => {
    if (controlledValue === prevControlledValueRef.current) return;
    prevControlledValueRef.current = controlledValue;

    if (controlledValue === undefined) {
      if (selectedItems.length) setSelectedItems([]);
      return;
    }

    const ids = Array.isArray(controlledValue) ? controlledValue : [controlledValue];
    const map = getAllItemsMap();

    // resolve from union map
    const resolved: T[] = ids.map((id) => map.get(id)).filter(Boolean) as T[];

    // preserve previously selected objects for unresolved ids
    if (resolved.length < ids.length && selectedItems.length) {
      const prevMap = new Map(selectedItems.map((it) => [getItemId(it), it] as [number | string, T]));
      ids.forEach((id) => {
        if (!map.has(id) && prevMap.has(id)) resolved.push(prevMap.get(id)!);
      });
    }

    // avoid churn if unchanged
    const same =
      resolved.length === selectedItems.length &&
      resolved.every((it, i) => getItemId(it) === getItemId(selectedItems[i]));

    if (!same) setSelectedItems(resolved);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue]); // intentionally NOT depending on `items`

  // Transform items to Select options format, ensuring no duplicate keys
  const options = useMemo(() => {
    // Create a Map to track unique items by their ID
    const uniqueItemsMap = new Map<number | string, T>();

    // First add all current API items
    items.forEach((item) => {
      uniqueItemsMap.set(getItemId(item), item);
    });

    // Then add selected items (this ensures selected items are always available)
    selectedItems.forEach((selectedItem) => {
      uniqueItemsMap.set(getItemId(selectedItem), selectedItem);
    });
    // Include any externally provided selected items without syncing state
    const ext = selectedItemsProvider?.();
    if (ext) {
      const arr = Array.isArray(ext) ? ext : [ext];
      arr.forEach((it) => uniqueItemsMap.set(getItemId(it), it));
    }

    // Convert Map values back to array and transform to options
    return Array.from(uniqueItemsMap.values()).map((item) => ({
      value: getItemId(item),
      label: getItemLabel(item),
      data: item,
    }));
  }, [items, selectedItems, selectedItemsProvider, getItemId, getItemLabel]);

  const defaultSearchPlaceholder =
    searchPlaceholder || t('common-search-min-chars-placeholder', { min: minSearchLength });

  return (
    <SearchSelectV2
      {...selectProps}
      options={options}
      loading={loading}
      hasMore={hasMore}
      totalCount={totalCount}
      currentCount={items.length}
      placeholder={placeholder}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      onOpenChange={handleOpenChange}
      onChange={(val) => {
        handleChange(val);
      }}
      optionRender={(option) => {
        const item = option.data.data as T;
        return renderOption ? renderOption(item) : <span>{getItemLabel(item)}</span>;
      }}
      inputSearchProps={{
        ...inputSearchProps,
        placeholder: inputSearchProps?.placeholder ?? defaultSearchPlaceholder,
      }}
    />
  );
}

export default GenericSearchSelect;
